import { alpha, lighten, ThemeOptions } from '@mui/material';
import { FIELD_SIZING } from './fieldSizing';
import { ICON_PX } from './iconSizes';

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        /** A control that names what it shows and opens a menu. */
        picker: true;
    }
}

type SharedOverrides = Pick<
    NonNullable<ThemeOptions['components']>,
    'MuiButton' | 'MuiMenu' | 'MuiMenuItem' | 'MuiListSubheader' | 'MuiTooltip'
>;

/**
 * The parts a picker is made of: the control that names what it currently
 * shows, and the menu that changes it.
 *
 * Held apart from any palette, so a surface with a palette of its own reads
 * the same as the rest of the app. Nothing here names a colour directly; the
 * theme these are applied to supplies them.
 */
export const SHARED_COMPONENT_OVERRIDES: SharedOverrides = {
    MuiButton: {
        styleOverrides: {
            sizeSmall: {
                padding: '4px',
                fontSize: '0.75rem',
                height: 'auto',
            },
            // As tall as a field at least, so a button in a column of
            // fields lines up with them.
            sizeMedium: {
                minHeight: FIELD_SIZING.height,
            },
            root: {
                textTransform: 'none',
                fontSize: 14,
                paddingTop: 6,
                paddingBottom: 6,
                lineHeight: 1.2,
            },
            // A panel's committing action: taller than a field and flat. A
            // contained primary one is lettered in the primary contrast
            // colour while enabled.
            sizeLarge: ({ theme }) => ({
                height: 42,
                paddingTop: 0,
                paddingBottom: 0,
                fontSize: 15,
                fontWeight: 500,
                boxShadow: 'none',
                '&.MuiButton-containedPrimary:not(.Mui-disabled)': {
                    color: theme.palette.primary.contrastText,
                },
            }),
            contained: ({ theme }) => ({
                color: theme.palette.common.white,
                '&.MuiButton-containedPrimary': {
                    color: theme.palette.common.white,
                },
                '&.Mui-disabled': {
                    color: theme.palette.text.disabled,
                },
            }),
            // A coloured button tints and fully borders in its colour on
            // hover. An `inherit` one keeps the divider border.
            outlined: ({ theme, ownerState }) => {
                const color =
                    ownerState.color === 'inherit'
                        ? undefined
                        : theme.palette[ownerState.color ?? 'primary'].main;
                return {
                    ...(color
                        ? {
                              '&:hover': {
                                  backgroundColor: alpha(color, 0.05),
                                  borderColor: color,
                              },
                          }
                        : {
                              borderColor: theme.palette.divider,
                              '&:hover': {
                                  backgroundColor: theme.palette.action.hover,
                                  borderColor: theme.palette.divider,
                              },
                          }),
                    '&.Mui-disabled': {
                        borderColor: theme.palette.divider,
                    },
                };
            },
        },
        variants: [
            {
                props: { variant: 'picker' },
                style: ({ theme }) => ({
                    // Shorter than a field: a picker sits in a console bar,
                    // beside the bar's other 28 px controls.
                    height: 28,
                    minHeight: 0,
                    minWidth: 0,
                    padding: theme.spacing(0, 1),
                    fontSize: 12,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    color: theme.palette.text.primary,
                    // Sunken, as every control the user picks into is: the
                    // fill is what parts a control from the bar it sits on,
                    // and the border alone cannot do that.
                    backgroundColor: theme.palette.background.sunken,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.shape.borderRadius,
                    transition: theme.transitions.create('border-color', {
                        duration: theme.transitions.duration.short,
                    }),
                    '&:hover': {
                        backgroundColor: theme.palette.background.sunken,
                        borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focusVisible': {
                        borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-disabled': {
                        color: theme.palette.text.disabled,
                        borderColor: theme.palette.divider,
                    },
                    '& .MuiButton-startIcon': {
                        color: theme.palette.primary.main,
                        marginRight: theme.spacing(0.75),
                    },
                    '& .MuiButton-endIcon': {
                        color: theme.palette.text.secondary,
                        marginLeft: theme.spacing(0.75),
                        transition: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shorter,
                        }),
                    },
                    // The chevron turns while the menu is up, so the control
                    // itself says which way its list went.
                    '&[aria-expanded="true"] .MuiButton-endIcon': {
                        transform: 'rotate(180deg)',
                    },
                }),
            },
        ],
    },

    MuiTooltip: {
        defaultProps: {
            enterDelay: 500,
            enterNextDelay: 500,
        },
        styleOverrides: {
            tooltip: ({ theme }) => ({
                maxWidth: 320,
                backgroundColor: alpha(
                    lighten(theme.palette.background.default, 0.1),
                    0.95
                ),
            }),
            popper: {
                zIndex: 1600, // Higher than Modal's 1501
            },
        },
    },

    MuiMenu: {
        defaultProps: {
            elevation: 1,
        },
        styleOverrides: {
            paper: ({ theme }) => ({
                borderRadius: theme.shape.borderRadius,
                marginTop: theme.spacing(0.5),
                padding: theme.spacing(0.5),
                border: `1px solid ${theme.palette.divider}`,
                // The paper's own padding is the whole inset; the list adds
                // none of its own above the first row or below the last.
                '& .MuiList-root': {
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(0.5),
                },
            }),
        },
    },

    // A heading over a group of rows in a menu: small, spaced capitals,
    // muted, so it labels the rows below it without reading as one of them.
    // It sits closer to its own rows than to the rule above it; at the top
    // of a menu, the paper's padding is the space above it.
    MuiListSubheader: {
        defaultProps: {
            disableSticky: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                padding: theme.spacing(1, 1, 0),
                '&:first-child': {
                    paddingTop: 0,
                },
                fontSize: 9,
                fontWeight: 400,
                lineHeight: '16px',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: theme.palette.text.secondary,
                backgroundColor: 'transparent',
            }),
        },
    },

    MuiMenuItem: {
        styleOverrides: {
            root: ({ theme }) => ({
                paddingTop: theme.spacing(0.5),
                paddingBottom: theme.spacing(0.5),
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
                fontSize: 12,
                fontWeight: 400,
                minHeight: 28,
                borderRadius: theme.shape.borderRadius,
                display: 'flex',
                gap: theme.spacing(1),
                '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-selected': {
                    backgroundColor: theme.palette.action.selected,
                    '&:hover': {
                        backgroundColor: theme.palette.action.selected,
                    },
                },
                // A row's icon is in the primary colour; a row that means
                // something else, such as a destructive one, sets its own.
                '& .MuiListItemIcon-root': {
                    minWidth: '16px',
                    color: theme.palette.primary.main,
                },
                // Every menu row's icon is 16 px, for 12 px text: the width
                // and height too, so neither an icon's own size nor an
                // icon set that sizes by attributes can change it. A mark
                // with a size of its own, such as a status, sits outside the
                // icon slot.
                '& .MuiListItemIcon-root > svg': {
                    fontSize: ICON_PX,
                    width: ICON_PX,
                    height: ICON_PX,
                },
                // The list's own gap already parts a rule from its rows.
                '& + .MuiDivider-root': {
                    marginTop: 0,
                    marginBottom: 0,
                },
                '& .MuiListItemText-root': {
                    margin: 0,
                },
                '& .MuiTypography-root': {
                    fontSize: 12,
                },
                '& .MuiListItemText-secondary': {
                    fontSize: 11,
                    color: theme.palette.text.secondary,
                },
            }),
        },
    },
};
